import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useContractorsApi } from '@/services/api';
import type { ContractorListItem } from '@ip/shared-types';
import { t } from '@/i18n';

interface ContractorPickerProps {
  companyNip: string;
  // value/onChange are optional; the RHF Controller in EmployeeForm passes them explicitly.
  value?: { contractorId?: string; contractorNip?: string } | null;
  onChange?: (v: { contractorId?: string; contractorNip?: string } | null) => void;
  disabled?: boolean;
}

const PL_MAP: Record<string, string> = { ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z' };

function normalizeSearch(text: string): string {
  return text.toLowerCase().replace(/[ąćęłńóśźż]/g, (c) => PL_MAP[c] ?? c).replace(/[^a-z0-9]/g, '');
}

function contractorLabel(c: ContractorListItem): string {
  return c.nip ? `${c.name} (NIP ${c.nip})` : (c.name ?? '—');
}

export function ContractorPicker({ companyNip, value, onChange, disabled }: ContractorPickerProps) {
  const emit = (v: { contractorId?: string; contractorNip?: string } | null) => onChange?.(v);
  const { getContractors } = useContractorsApi();
  const [contractors, setContractors] = useState<ContractorListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!companyNip) return;
    let cancelled = false;
    setLoading(true);
    getContractors(companyNip)
      .then((c) => { if (!cancelled) setContractors(c); })
      .catch(() => { if (!cancelled) toast.error(t('errors.loadingFailed')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyNip, getContractors]);

  const selected = useMemo(
    () => contractors.find((c) => c.id === value?.contractorId) ?? null,
    [contractors, value?.contractorId],
  );

  const filtered = useMemo(() => {
    if (!search) return contractors;
    const needle = normalizeSearch(search);
    return contractors.filter(
      (c) => normalizeSearch(c.name || '').includes(needle) || normalizeSearch(c.nip || '').includes(needle),
    );
  }, [contractors, search]);

  const typedNip = (search.match(/^\d{10}$/) || [])[0];
  const matchesExisting = typedNip ? contractors.some((c) => c.nip === typedNip) : false;

  const hasValue = Boolean(selected || value?.contractorNip);
  const triggerLabel = selected
    ? contractorLabel(selected)
    : value?.contractorNip
      ? `NIP ${value.contractorNip}`
      : t('employees.employment.contractor.searchPlaceholder');

  const handleSelect = (id: string) => {
    emit({ contractorId: id });
    setSearch('');
    setOpen(false);
  };

  const handleCreateFromBir = () => {
    if (!typedNip) return;
    emit({ contractorNip: typedNip });
    setSearch('');
    setOpen(false);
  };

  const handleClear = () => {
    emit(null);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {loading ? <Loader2 className="size-4 animate-spin" /> : triggerLabel}
          </span>
          <span className="flex items-center gap-0.5">
            {hasValue && !disabled && (
              <span
                role="button"
                tabIndex={0}
                aria-label={t('common.clear')}
                className="hover:bg-accent rounded p-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    handleClear();
                  }
                }}
              >
                <X className="size-3" />
              </span>
            )}
            <ChevronsUpDown className="size-3.5 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('employees.employment.contractor.searchPlaceholder')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{t('contractors.noContractors')}</CommandEmpty>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem key={c.id} value={c.id} onSelect={() => handleSelect(c.id)}>
                  <Check className={cn('size-4', value?.contractorId === c.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{contractorLabel(c)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {typedNip && !matchesExisting && (
            <div className="border-t p-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                disabled={disabled}
                onClick={handleCreateFromBir}
              >
                {t('employees.employment.contractor.createFromBir', { nip: typedNip })}
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
